/**
 * COBERTURA por nó e FUNIL por área — as derivações de exibição da aba Processos.
 *
 * O backend já entrega tudo pronto e deduplicado (`/orquestrador/cobertura`): os
 * três níveis com seus denominadores distintos, o funil com a identidade fechada e
 * as listas de empresa de cada fatia. Aqui NÃO se recalcula percentual nenhum — o
 * front só formata e indexa.
 *
 * Isso é deliberado. A dedup por empresa já existe em dois lugares
 * (`derivarProcessos` no front, `_no_do_grupo` no BFF) e esta frente não aumenta
 * esse risco: a contagem nasce no servidor e chega pronta. Recalcular aqui abriria
 * de novo a porta para o nó dizer um número e o popup listar outro, que é o bug
 * histórico desta tela.
 *
 * AS TRÊS PORCENTAGENS TÊM DENOMINADORES DIFERENTES, e é por isso que `rotuloBase`
 * existe: todo percentual é exibido junto do seu denominador, nunca sozinho.
 */

import type {
  IAreaCobertura,
  ICoberturaResposta,
  IEtapaFunil,
  IFatiaCobertura,
  IGrupoCobertura,
  IMicroCobertura,
} from '../api';

/** Percentual formatado em pt-BR, ou "—" quando não há denominador confiável. */
/**
 * 100% SÓ QUANDO É 100% (08/10, achado ao investigar o "344 de 345").
 *
 * `maximumFractionDigits: 0` arredondava 99,71% para "100%", e o cabeçalho do
 * Lucro Presumido afirmava `100% · 344 de 345 empresas` — a tela dizendo
 * "todas" com uma empresa de fora. Quem lê o número grande e não confere o
 * "de N" conclui que não falta ninguém.
 *
 * É o mesmo defeito que a tela já corrigiu no outro extremo em 25/09 ("0%" vs
 * "sem execução"): o arredondamento inventando uma afirmação forte.
 *
 * Agora 99,71% imprime "99%" (piso, não arredondamento) e só o 100 exato sai
 * como "100%". O mesmo vale para o 0: 0,4% vira "1%" em vez de sumir em "0%",
 * que afirmaria "ninguém" tendo alguém.
 */
export const fmtPct = (pct: number | null | undefined): string => {
  if (pct === null || pct === undefined) return '—';
  let inteiro = Math.round(pct);
  if (inteiro === 100 && pct < 100) inteiro = 99;
  if (inteiro === 0 && pct > 0) inteiro = 1;
  return `${inteiro.toLocaleString('pt-BR')}%`;
};

/**
 * O texto da BOLHA acima do nó. Curto de propósito (cabe em 22px): só o inteiro.
 * O denominador vai no `title`, nunca omitido — ver `tituloDaBolha`.
 *
 * A REGRA DOS NÓS VAZIOS (18/09). `denominador === 0` é "não passou ninguém por
 * aqui", e isso é uma AFIRMAÇÃO — imprime `0%`. Só `percentual === null` COM
 * denominador desconhecido é "não sei", e aí sai `—`.
 *
 * Até aqui os dois caíam no `—` do `fmtPct`, e a tela ficava inconsistente com ela
 * mesma: "Apuração LP" mostrava `0%` e "Importação de notas LP", no mesmo estado
 * ("sem empresas nesta rota"), não mostrava nada. "Sem porcentagem" e "zero por
 * cento" são informações diferentes e o usuário precisa distinguir "não passou
 * ninguém" de "não sei".
 */
export const textoDaBolha = (fatia: IFatiaCobertura | null | undefined): string => {
  if (!fatia) return '—';
  if (fatia.percentual === null && fatia.denominador === 0) return '0%';
  return fmtPct(fatia.percentual);
};

/**
 * Os DOIS estados de 0%, que são operacionalmente OPOSTOS (21/09).
 *
 * O backend publica `entraram` ao lado de `cobertura` exatamente para separá-los,
 * e até aqui a bolha imprimia `0%` nos dois:
 *
 *   - `entraram === 0`  → NINGUÉM entrou: não houve trabalho no período. Dizer
 *     "0%" aqui sugere fracasso onde não houve tentativa, e foi o que o dono
 *     apontou ("tem nó que não rodou nada mas aparece como 0%");
 *   - `entraram > 0`    → entraram e TRAVARAM: 0% é o número certo, e é uma
 *     informação forte — ninguém concluiu.
 *
 * A distinção NÃO volta a ser "0% vs —" (isso apagaria a correção de 18/09, que
 * separou "não passou ninguém" de "não sei"): o texto segue `0%` no caso que
 * merece, e o caso sem trabalho vira `·` com o motivo no tooltip.
 *
 * QUARTO ESTADO (22/09) — "este bot não reporta etapas". Medido em produção: a
 * bolha conta `bot_modulo_eventos` e o rodapé conta `bot_unit_result`. O
 * `BOT_APURACAO_LUCRO_PRESUMIDO` tem **0 eventos e 348 unidades** (não declara
 * catálogo de módulos), e o `BOT_APURACAO_FISCAL`, 0 e 9030. Esses nós exibiam
 * `·` ("não houve execução") com centenas de empresas na linha de baixo — as duas
 * leituras certas sobre coisas diferentes, que é a família de bug "card e drawer
 * contam coisas diferentes". O `reporta_modulos` do payload separa os dois.
 *
 * Quatro estados legíveis, nenhum silencioso:
 *
 *   `—`  não sei (espelho indisponível / sem cobertura conhecida)
 *   `·`  não houve execução no período
 *   `⊘`  este bot não reporta etapas (o resultado está no rodapé)
 *   `0%` rodou e nenhuma concluiu
 */
export const SEM_EXECUCAO = '·';
export const SEM_REPORTE = '⊘';

export const bolhaDoNo = (
  cobertura: IFatiaCobertura | null | undefined,
  entraram: IFatiaCobertura | null | undefined,
  reportaModulos?: boolean,
): string => {
  const texto = textoDaBolha(cobertura);
  // Só reinterpreta o `0%`; `—` e qualquer percentual > 0 passam intactos.
  if (texto !== '0%') return texto;
  // `entraram` ausente = payload antigo: mantém o `0%` de sempre em vez de
  // inventar um estado que o dado não sustenta.
  if (!entraram) return texto;
  if (entraram.quantidade !== 0) return texto;
  // Silêncio EXPLICADO: o bot não emite evento de módulo, então a ausência aqui
  // não diz nada sobre ter rodado ou não — o rodapé é que sabe. `undefined`
  // (mon antigo) cai no `·` de sempre: ausência de campo não vira afirmação.
  return reportaModulos === false ? SEM_REPORTE : SEM_EXECUCAO;
};

/** O tooltip do estado acima — nenhum símbolo aparece sem explicação. */
export const tituloDoNo = (
  cobertura: IFatiaCobertura | null | undefined,
  entraram: IFatiaCobertura | null | undefined,
  contexto: string,
  reportaModulos?: boolean,
  qtdBots?: number,
): string => {
  const estado = bolhaDoNo(cobertura, entraram, reportaModulos);
  if (estado === SEM_REPORTE) {
    // Grupo SEM BOT cai no mesmo `reporta_modulos: false` — `any()` de lista
    // vazia é falso. O número está certo nos dois casos, mas o texto não podia
    // dizer "este bot não reporta" onde não existe bot nenhum. Achado da
    // revisão adversarial, não de teste de suíte.
    if (qtdBots === 0) {
      return `${contexto} — nenhum bot está associado a este grupo no cadastro`;
    }
    return (
      `${contexto} — este bot não reporta as etapas do processo, então não há ` +
      'percentual de avanço. O resultado por empresa está no rodapé do nó.'
    );
  }
  if (estado === SEM_EXECUCAO) {
    return `${contexto} — nenhuma empresa entrou neste nó no período selecionado`;
  }
  return tituloDaBolha(cobertura, contexto);
};

/**
 * O tooltip da bolha: o percentual COM o seu denominador explícito.
 *
 * "87% — 305 de 350 empresas da área". Sem a segunda metade o número é ambíguo
 * entre os três níveis, e essa ambiguidade é exatamente o que o pedido do dono
 * ("lembrando de diferenciar as porcentagens... Macro, micro e nano") proíbe.
 */
export const tituloDaBolha = (
  fatia: IFatiaCobertura | null | undefined,
  contexto: string,
): string => {
  if (!fatia || fatia.percentual === null || !fatia.denominador) {
    // TRÊS motivos diferentes para não haver percentual, e confundi-los faz a tela
    // acusar um defeito que não existe:
    //  - denominador ZERO: ninguém passou por esta rota. O espelho está ótimo.
    //    É afirmação, e a bolha imprime `0%` (ver `textoDaBolha`);
    //  - denominador AUSENTE (null/undefined): espelho de vigência indisponível —
    //    o único caso de "não sei";
    //  - fatia ausente: o nó não tem cobertura conhecida (ver `bolhaIndisponivel`).
    if (!fatia) {
      return `${contexto} — sem cobertura conhecida para este nó`;
    }
    return fatia.denominador === 0
      ? `${contexto} — sem empresas nesta rota (0%)`
      : `${contexto} — percentual não confiável (espelho de vigência indisponível)`;
  }
  // "CONCLUÍRAM", não "processaram" (05/10, pedido do dono).
  //
  // O numerador é `passaram` do backend: empresas com ao menos um evento
  // `concluido`. Quem só FALHOU não entra — e é por isso que a bolha podia dizer
  // "248 de 349" enquanto o rodapé somava 349 (medido em 05/10: as 101 que
  // faltavam eram todas `err`). O número sempre esteve certo; faltava a palavra.
  //
  // O tooltip passa a dizer as duas coisas: quantas concluíram e quantas não —
  // sem isso o usuário fecha a conta de cabeça e acha que a tela se contradiz.
  const naoConcluiram = fatia.denominador - fatia.quantidade;
  const base =
    `${contexto}: ${fmtPct(fatia.percentual)} — ${fatia.quantidade} de ` +
    `${fatia.denominador} ${fatia.base} concluíram esta etapa${
      naoConcluiram > 0
        ? `; as outras ${naoConcluiram} entraram mas não concluíram (falha, alerta ou ainda em curso)`
        : ''
    }`;
  // LISTA CORTADA PELO BACKEND (24/09). `_MAX_DRILLDOWN` corta o drill-down em
  // 2000 empresas e sinaliza em `truncada` — e nenhum consumidor lia o campo: o
  // texto afirmava "310 de 354 empresas" enquanto a lista trazia menos, sem nada
  // dizendo.
  //
  // Hoje o corte NAO acontece (medido em 24/09: a maior fatia tem 654 empresas
  // de um teto de 2000, e o espelho inteiro tem 1594). Mas o dia em que
  // acontecer e' justamente o dia em que ninguem vai desconfiar do numero — e o
  // backend gastou o trabalho de sinalizar para alguem ler.
  //
  // Aqui, e nao no popup: o aviso passa a valer para TODO consumidor do titulo.
  return fatia.truncada ? `${base} (lista limitada a ${fatia.listadas})` : base;
};

/**
 * A bolha do nó SEM percentual conhecido — o estado explícito que substituiu a
 * ausência (18/09).
 *
 * A REGRA, aplicada a TODOS os nós:
 *  - nó com dado            → sempre mostra a bolha com o percentual;
 *  - nó sem empresas        → mostra `0%` (afirmação: não passou ninguém);
 *  - percentual indisponível→ mostra `—` e o tooltip diz por quê.
 *
 * O que NÃO é mais permitido é a bolha sumir. Ausência não é um estado legível:
 * o usuário não distingue "não carregou" de "não se aplica" de "bug", e foi
 * exatamente essa dúvida que ele circulou com "?" nas capturas — nós com 277 e
 * 193 empresas sem bolha nenhuma, ao lado de nós com 281 mostrando 86%.
 *
 * `onSelect` é um no-op: não há lista para abrir quando não há fatia. A bolha
 * segue clicável só para não virar um alvo morto que parece quebrado.
 */
export const bolhaIndisponivel = (
  contexto: string,
  motivo: string,
): { texto: string; title: string; formato: 'pct'; onSelect: () => void } => ({
  texto: '—',
  title: `${contexto} — sem percentual: ${motivo}`,
  formato: 'pct',
  onSelect: () => {},
});

/** Índice área→dados, para o board casar a linha do `/esteira` com a cobertura. */
export const indexarAreas = (
  dados: ICoberturaResposta | undefined,
): Map<string, IAreaCobertura> => {
  const mapa = new Map<string, IAreaCobertura>();
  (dados?.areas ?? []).forEach(a => {
    // A chave é o NOME do departamento: é o que `ILinhaEsteira.departamento`
    // carrega, e o id não viaja na linha da esteira. Área sem nome não é
    // endereçável e fica de fora (o board cai no estado sem-percentual).
    if (a.nome) mapa.set(a.nome, a);
  });
  return mapa;
};

/** Índice bot→micro dentro de uma área. */
export const indexarBots = (area: IAreaCobertura | undefined): Map<string, IMicroCobertura> => {
  const mapa = new Map<string, IMicroCobertura>();
  (area?.micro ?? []).forEach(m => mapa.set(m.bot, m));
  return mapa;
};

/**
 * Índice grupo_id→cobertura do grupo (21/09).
 *
 * Indexa por `grupo_id`, não por nome: nome de grupo se repete entre áreas
 * ("Apuração" existe em LP e em SN) e casar por nome misturaria a cobertura de
 * uma área com a de outra — o mesmo erro de identidade que o `chaveBotArea`
 * abaixo existe para evitar no nível do bot. O id vem do cadastro e é único.
 *
 * Grupo sem id (cadastro incompleto) fica de fora: sem identidade não há como
 * casar com o nó, e um `undefined` no lookup produz o "—" explicado, que é o
 * comportamento certo para "não sei", em vez de um número de outro grupo.
 */
export const indexarGrupos = (area: IAreaCobertura | undefined): Map<string, IGrupoCobertura> => {
  const mapa = new Map<string, IGrupoCobertura>();
  (area?.grupos ?? []).forEach(g => {
    if (g.grupo_id) mapa.set(g.grupo_id, g);
  });
  return mapa;
};

/**
 * Chave de `indexarMicroPorArea`. `\n` separa porque não ocorre em id de
 * departamento (uuid4 hex) nem em nome de bot — `:` e `-` ocorrem nos dois.
 */
export const chaveBotArea = (bot: string, departamentoId: string | null): string =>
  `${bot}\n${departamentoId ?? ''}`;

/**
 * Índice (bot, área)→micro: DESEMPATA o bot que pertence a duas áreas.
 *
 * O índice global do board colapsa esse bot numa entrada só e o marca ambíguo —
 * fora de contexto as duas coberturas são respostas igualmente válidas, com
 * denominadores diferentes, e escolher uma faria metade dos usuários ler o número
 * errado. Mas quando o nó está DENTRO de um grupo o contexto existe: o grupo
 * pertence a uma área, e ali a cobertura daquela área é a única correta.
 *
 * Medido em produção (17/09/2026): 6 bots fiscais vivem em Fiscal LP E Fiscal SN
 * ao mesmo tempo (EFD_REINF, WS_ECAC_UNIFIED, ORGANIZA_GUIAS_AUTMAIS,
 * ANEXA_RELATORIO_DMS_ACESSORIAS, WS_ANEXA_RELATORIO_REST_ACESSORIAS,
 * AUDITORIA_RELATORIO_DCTFWEB). Eram 6 dos principais nós fiscais exibindo `—`
 * permanentemente TENDO passagem real — o nó "Transmissão dos Impostos LP"
 * mostrava `—` com 178 empresas na linha logo abaixo.
 *
 * Nenhum percentual NOVO nasce aqui: o backend já entrega `passaram_bot ∩ universo`
 * por área (medido: 175/222 = 78,8% no nó LP). É o número que já existia, agora
 * alcançável.
 */
export const indexarMicroPorArea = (
  dados: ICoberturaResposta | undefined,
): Map<string, IMicroCobertura> => {
  const mapa = new Map<string, IMicroCobertura>();
  (dados?.areas ?? []).forEach(a => {
    a.micro.forEach(m => mapa.set(chaveBotArea(m.bot, a.departamento_id), m));
  });
  return mapa;
};

/**
 * O micro do bot NO CONTEXTO da área aberta; `null` quando não há resposta única.
 *
 * `departamentoId` null = sentinela ("Todos os bots"/"Sem grupo") ou grupo sem
 * departamento: aí não há contexto para desempatar e o bot multi-área continua
 * sem percentual, como antes. Fora do multi-área nada muda — o índice global
 * responde igual.
 */
export const microNoContexto = (
  bot: string,
  departamentoId: string | null,
  porArea: Map<string, IMicroCobertura>,
  global: Map<string, { micro: IMicroCobertura; ambiguo: boolean }>,
): IMicroCobertura | null => {
  if (departamentoId !== null) {
    const noContexto = porArea.get(chaveBotArea(bot, departamentoId));
    if (noContexto) return noContexto;
  }
  const achado = global.get(bot);
  return achado && !achado.ambiguo ? achado.micro : null;
};

/** Índice módulo→etapa do funil de um bot. */
export const indexarEtapas = (micro: IMicroCobertura | undefined): Map<number, IEtapaFunil> => {
  const mapa = new Map<number, IEtapaFunil>();
  (micro?.funil?.etapas ?? []).forEach(e => mapa.set(e.modulo_id, e));
  return mapa;
};

/**
 * O resumo do funil em uma linha, para a meta do nó de bot.
 * "6 entraram · 3 chegaram ao final · 3 pararam no caminho"
 *
 * `pararam` aqui é a soma dos que pararam em CADA etapa — é quantas paradas o
 * fluxo teve, e responde "quantas pararam no meio do caminho". Note que NÃO é
 * `entraram - chegaram` por subtração: cada parcela vem da sua própria contagem,
 * e a soma é de contagens exatas, nunca de percentuais arredondados.
 */
export const resumoDoFunil = (micro: IMicroCobertura | undefined): string => {
  const funil = micro?.funil;
  if (!funil || !funil.etapas.length) return '';
  const pararam = funil.etapas.reduce((acc, e) => acc + e.pararam.quantidade, 0);
  const partes = [
    `${funil.entraram_no_bot} entraram`,
    `${funil.chegaram_ao_final.quantidade} chegaram ao final`,
  ];
  if (pararam > 0) partes.push(`${pararam} pararam no caminho`);
  return partes.join(' · ');
};

/**
 * A identidade do funil, reconferida NA TELA antes de desenhar.
 *
 * O backend já garante e o BFF já reconfere; esta terceira checagem existe porque
 * é barata e porque a tela é o último lugar onde um número errado ainda pode ser
 * suprimido. Quebrou = a aba esconde o funil e diz por quê, em vez de desenhar
 * uma progressão que não fecha.
 */
export const funilFecha = (micro: IMicroCobertura | undefined): boolean =>
  (micro?.funil?.etapas ?? []).every(
    e => e.entraram.quantidade === e.avancaram.quantidade + e.pararam.quantidade,
  );

/**
 * A área cobre pelo menos tanto quanto o melhor dos seus bots?
 *
 * A promessa do dono é "quando ele estiver em 100%, a área também chega a 100%".
 * A IGUALDADE só vale quando um bot sozinho atende a área inteira — com dois bots
 * cobrindo metades diferentes, nó em 100% e área em 60% é correto e esperado. O
 * que é sempre verdade é a DESIGUALDADE (a área é a UNIÃO dos bots), e é ela que
 * a tela verifica: união menor que a parte seria denominador trocado.
 */
export const areaCoerente = (area: IAreaCobertura | undefined): boolean => {
  const micros = area?.micro ?? [];
  if (!micros.every(m => m.cobertura.quantidade <= (area?.cobertura.quantidade ?? 0))) {
    return false;
  }
  // `entraram` entra na checagem desde 18/09 — a grandeza em que o bug relatado
  // vivia. A área em 0 com o nó em 21 passava batido porque só `cobertura` era
  // comparada. `entraram` é opcional (BFF antigo na janela de deploy): ausente,
  // não há o que comparar e a checagem não inventa uma falha.
  const daArea = area?.entraram?.quantidade;
  if (daArea !== undefined && !micros.every(m => m.entraram.quantidade <= daArea)) {
    return false;
  }
  // Quem CONCLUIU necessariamente ENTROU — cobertura acima de entraram é
  // impossível por construção e denuncia universos diferentes entre os níveis.
  if (daArea !== undefined && (area?.cobertura.quantidade ?? 0) > daArea) return false;
  // `m.entraram` é obrigatório no tipo, mas o tipo descreve o BFF NOVO: em runtime
  // um payload antigo pode não trazê-lo. Ausente, pula — nunca vira 0, que
  // reprovaria qualquer bot com cobertura > 0 e esconderia o funil sem motivo.
  return micros.every(
    m => m.entraram?.quantidade === undefined || m.cobertura.quantidade <= m.entraram.quantidade,
  );
};

/**
 * O texto que vai ao lado do NOME DA ÁREA no board — o "X %" que o dono escreveu
 * à mão na tela anotada dele.
 *
 * Sem espelho não há número: a tela diz isso em vez de imprimir um percentual que
 * não se sustenta (o fail-open do backend existe para não travar a frota de bots;
 * como denominador ele seria mentira silenciosa).
 */
export const rotuloDaArea = (area: IAreaCobertura | undefined): string => {
  if (!area) return '';
  // Universo ZERO não é espelho quebrado: é o usuário restrito olhando uma área
  // em que não tem empresa nenhuma. Dizer "percentual não confiável" ali faria a
  // tela denunciar um defeito inexistente — e o usuário abriria chamado por isso.
  if (area.universo === 0) return 'nenhuma empresa sua nesta área';
  if (area.cobertura.percentual === null) return 'percentual não confiável';
  return `${fmtPct(area.cobertura.percentual)} · ${area.cobertura.quantidade} de ${area.universo} empresas`;
};

/**
 * A área concluiu ZERO mas teve empresa ENTRANDO nos bots dela?
 *
 * Este é o estado do bug relatado em 18/09: "Departamento Pessoal — 0% · 0 de 695
 * empresas" com o nó da área exibindo "21 empresas". Os dois números estavam
 * certos e mediam coisas diferentes (`cobertura` conta quem CONCLUIU, o nó
 * mostrava quem ENTROU), mas lado a lado na mesma tela liam-se como contradição.
 *
 * "Ninguém entrou" e "entraram 21 e nenhuma concluiu" são situações
 * operacionalmente OPOSTAS — a primeira não pede ação, a segunda é uma represa.
 * Imprimir "0%" nas duas apaga justamente a diferença que importa.
 */
export const areaTravada = (area: IAreaCobertura | undefined): boolean =>
  Boolean(
    area &&
      area.universo > 0 &&
      area.cobertura.percentual !== null &&
      area.cobertura.quantidade === 0 &&
      (area.entraram?.quantidade ?? 0) > 0,
  );

/**
 * As ressalvas da área, exibidas junto do percentual — nunca escondidas.
 *
 * As duas dizem que o denominador não é o óbvio, e omiti-las faria o usuário ler
 * um número correto como se fosse outro:
 * - `sem_regime_definido`: empresas vigentes que ficaram FORA do denominador por
 *   não terem regime cadastrado (a área filtra por regime e não se sabe se elas
 *   deveriam passar);
 * - `regime_ambiguo`: os bots da área declaram regimes divergentes, então a área
 *   não tem universo único e o percentual é sobre TODAS as vigentes.
 */
export const ressalvasDaArea = (area: IAreaCobertura | undefined): string[] => {
  const notas: string[] = [];
  if (!area) return notas;
  if (area.regime_ambiguo) {
    notas.push(
      'bots desta área declaram regimes diferentes — sem universo único, o percentual é sobre todas as empresas vigentes',
    );
  }
  // ÁREA TRAVADA vem primeiro: é a ressalva ACIONÁVEL. As outras duas explicam o
  // denominador; esta diz que houve trabalho e ele não terminou.
  if (areaTravada(area)) {
    notas.push(
      `nenhuma empresa concluiu nesta área, mas ${area.entraram?.quantidade ?? 0} entraram nos bots dela — o 0% é trabalho parado, não ausência de trabalho`,
    );
  }
  // A ressalva do "sem regime" SÓ vale para área COM filtro de regime. Numa área
  // sem filtro as empresas sem regime ENTRAM normalmente no denominador (é o que o
  // próprio texto implica), então exibi-la ali seria mentira — o backend já manda
  // `sem_regime_definido: 0` nesse caso (`vigentes_no_regime` devolve conjunto
  // vazio quando `regime is None`), e a guarda por `regime` torna a tela correta
  // mesmo se essa origem regredir. Cinto e suspensório num texto que o usuário lê
  // como afirmação sobre o denominador dele.
  if (area.sem_regime_definido > 0 && area.regime !== null) {
    notas.push(
      `${area.sem_regime_definido} empresa(s) vigente(s) sem regime definido ficaram fora do denominador desta área`,
    );
  }
  return notas;
};
